import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Spinner, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

// Asumsi path ini sudah benar
import PropertySelectComponent from './housekeeping-forecast/PropertySelectComponent';
import RoomSelectComponent from './housekeeping-forecast/RoomSelectComponent';
import DateRangePickerComponent from './housekeeping-forecast/DateRangePickerComponentV2';
import HousekeepingForecastTableReport from './housekeeping-forecast/HousekeepingForecastTableReport';
import { useQueryClient } from '@tanstack/react-query';
import { FilterSchema, FilterSchemaType } from '../../../services/housekeeping-forecast-report/schema/BookingSchema';
import useHousekeepingReport from '../../../services/housekeeping/hooks/useHousekeepingReport';

const HousekeepingForecastReportComponent: React.FC = () => {
    const { control, handleSubmit, formState: { errors }, setValue, watch } = useForm<FilterSchemaType>({
        resolver: zodResolver(FilterSchema),
        defaultValues: {
            filter: {
                // property_id: "",
                periode: {
                    start: "",
                    end: "",
                }
                // room_type_id: "",
            },
        },
    });

    // State untuk menyimpan filter yang akan digunakan untuk fetching data
    const [activeFilter, setActiveFilter] = useState<FilterSchemaType["filter"] | null>(null);

    const queryClient = useQueryClient();

    const currentFormValues = watch();

    const isFormChanged = activeFilter
        ? currentFormValues.filter?.periode?.start !== activeFilter.periode?.start ||
          currentFormValues.filter?.periode?.end !== activeFilter.periode?.end
        : false;

    // 1. Menggunakan hook untuk fetch data
    // Query hanya akan aktif (enabled) jika activeFilter sudah di-set (setelah submit)
    const {
        data: reportData,
        isFetching,
        isError,
        error
    } = useHousekeepingReport({
        filter: activeFilter,
        search: "",
        limit: 1,
        all: 1
    });

    const showRefresh = !isFetching && reportData && activeFilter !== null && !isFormChanged;

    // 2. Memetakan data dari API ke format yang dibutuhkan oleh tabel
    // `useMemo` digunakan untuk efisiensi, agar mapping tidak berjalan di setiap render
    const mappedData = useMemo(() => {
        if (!reportData?.data) return [];

        return reportData.data.map(item => ({
            // Kiri: Key yang dibutuhkan Tabel, Kanan: Key dari API
            date: item.date_housekeeping,
            total_room: Number(item.total_rooms),
            room_reserverd: Number(item.room_reserved), // Perhatikan typo 'reserverd' agar cocok dengan dummy
            occupancy: parseFloat(item.occupancy),
            arrival_rooms: Number(item.arrival_room),
            in_house_rooms: Number(item.in_house_room),
            departure_rooms: Number(item.departure_room),
            adult_in_house: Number(item.total_adults),
            children_in_house: Number(item.total_childs),
            cancelled_room: Number(item.cancel_room),
            maintenance: Number(item.total_room_maintenance),
        }));
    }, [reportData]); // Dependensi: hanya kalkulasi ulang jika reportData berubah

    // Fungsi yang dijalankan saat form di-submit
    const onSubmit = useCallback((data: FilterSchemaType) => {
        // Validasi tambahan sebelum submit jika perlu
        if (!data.filter.periode.start || !data.filter.periode.end) {
            toast.error("Please select a valid date range.");
            return;
        }

        if (showRefresh) {
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey;
                    return Array.isArray(key) && key[0] === 'housekeeping-report';
                }
            });
        }

        // Set filter aktif, yang akan memicu React Query untuk fetch data
        setActiveFilter(data.filter);
    }, [showRefresh, queryClient]);

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetching && reportData) {
            toast.success('Housekeeping forecast data loaded successfully.');
        }
    }, [isFetching, reportData]);

    // Komponen untuk merender konten berdasarkan state fetching
    const renderReportContent = () => {
        // Jika filter belum pernah di-submit, jangan tampilkan apa-apa
        if (!activeFilter) {
            return (
                <Alert variant="info" className="mt-4 text-center">
                    Please select your filters and click "Show Forecast" to view the report.
                </Alert>
            );
        }

        // Tampilkan spinner saat data sedang diambil
        if (isFetching) {
            return (
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Generating Report...</p>
                </div>
            );
        }

        // Tampilkan pesan error jika terjadi kesalahan
        if (isError) {
            return (
                <Alert variant="danger" className="mt-4">
                    <strong>Error:</strong> Failed to load report data. {(error as Error)?.message || ''}
                </Alert>
            );
        }

        // Tampilkan tabel jika data berhasil diambil dan ada isinya
        if (mappedData.length > 0) {
            return (
                <HousekeepingForecastTableReport
                    data={{
                        data: mappedData,
                        success: reportData?.success ?? true,
                        message: reportData?.message ?? "Data loaded successfully",
                        code: reportData?.code ?? 200,
                    }}
                />
            );
        }

        // Tampilkan pesan jika data kosong
        if (reportData && mappedData.length === 0) {
            return (
                <Alert variant="secondary" className="mt-4 text-center">
                    No housekeeping data found for the selected criteria.
                </Alert>
            );
        }

        return null;
    };

    return (
        <Container fluid className="dashboard-content">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                    {/* Kiri: Filter Components */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="w-100">
                            <PropertySelectComponent setValue={setValue} />
                        </div>
                        <div className="w-100">
                            <RoomSelectComponent control={control} errors={errors} setValue={setValue} />
                        </div>
                    </div>

                    {/* Kanan: Date Range & Submit */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100 align-items-end">
                        <div className="w-100">
                            <DateRangePickerComponent control={control} errors={errors} setValue={setValue} />
                        </div>
                        <div className="w-100 w-md-auto">
                            <Button variant={showRefresh ? "info" : "warning"} type="submit" className="w-100" disabled={isFetching}>
                                {isFetching ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                        {' '}Loading...
                                    </>
                                ) : showRefresh ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Forecast</>
                                ) : (
                                    'Show Forecast'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>

            {/* 3. Render tabel atau pesan status yang sesuai */}
            <div className="mt-4">
                {renderReportContent()}
            </div>
        </Container>
    );
};

export default HousekeepingForecastReportComponent;